import React from 'react';
import { useInterval } from 'usehooks-ts';

import {
  AddItem,
  AddTask,
  GetTaskById,
  RemoveItem
} from '../../utils/game-engine/assets';
import {
  AdjustScore,
  GiveScore,
  HideTask,
  RemoveScore,
  ShowHideZoneArea,
  ShowTask
} from '../../utils/game-engine/player';
import { Task } from '../../types/entities/task';
import { GameContext } from '../../contexts/game';
import { useTimers } from '../../hooks/use-game-timer';
import { isNetworkOnline } from '../../utils/indicator';
import { useGameToast } from '../../hooks/use-game-toast';
import ListenerManager from '../../utils/listener-manager';
import { postPlayerState } from '../../services/syncronize-state';
import { giveScore as teamGiveScore } from '../../services/teams';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { PlayerContext, PlayerContextState } from '../../contexts/player';
import { mergeServerPlayerState } from '../../utils/game-engine/player-merge';
import { AddTitle, RemoveTitle } from '../../utils/game-engine/assets/titles';

export const Player: React.FC = () => {
  const [player, setPlayer] = React.useContext(PlayerContext);
  const [, , hubConnection] = React.useContext(GameContext);

  const playerState = player.playerState;
  const playerCode = playerState?.code;

  const timers = useTimers();

  const {
    addItemToast,
    addScoreToast,
    addTitleToast,
    removeItemToast,
    removeScoreToast,
    removeTitleToast
  } = useGameToast();

  const addRemoveItemHandler = async (
    action: 'add' | 'remove',
    message: any
  ) => {
    setPlayer((prev) => {
      const payload = {
        id: message.id,
        quantity: parseInt(message.quantity)
      };

      const response =
        action === 'add' ? AddItem(prev, payload) : RemoveItem(prev, payload);
      return UpdatePlayerStateAsync(prev, response);
    });

    if (action === 'add') addItemToast(message.id, message.quantity);
    if (action === 'remove') removeItemToast(message?.id, message?.quantity);
  };

  const showHideObjectHandler = async (
    action: 'showTask' | 'hideTask' | 'showArea' | 'hideArea',
    message: any
  ) => {
    setPlayer((prev) => {
      if (prev.playerState) {
        let response = prev.playerState;

        if (action === 'showTask') response = ShowTask(prev, message?.id!);
        if (action === 'hideTask') response = HideTask(prev, message?.id!);
        if (action === 'showArea')
          response = ShowHideZoneArea(prev, message?.id!, true);
        if (action === 'hideArea')
          response = ShowHideZoneArea(prev, message?.id!, false);

        return UpdatePlayerStateAsync(prev, response);
      }

      return prev;
    });
  };

  const addScoreHandler = async (message: any) => {
    if (message.listIds) {
      setPlayer((prev) => {
        let response = prev.playerState;
        if (!response) return prev;

        const { tasks, score: prevScore = 0 } = response;
        if (!tasks) return prev;

        const [taskId, formId] = message.listIds;
        const quantity = parseInt(message?.quantity);
        if (isNaN(quantity)) return prev;

        const taskIndex = tasks.findIndex((t) => t.id === taskId);
        if (taskIndex === -1) return prev;

        const task = tasks[taskIndex];
        let prevAnswerScore = 0;

        const isArTask =
          taskId === formId ||
          (!task.taskContentFormAnswers?.some((f) => f.formId === formId) &&
            Boolean(task.dataARMiniGame));

        // AR task
        if (isArTask) {
          prevAnswerScore = task.dataARMiniGame?.score ?? 0;
        }
        // form task
        else if (task.taskContentFormAnswers?.length) {
          const formIndex = task.taskContentFormAnswers.findIndex(
            (f) => f.formId === formId
          );

          if (formIndex === -1) return prev;

          prevAnswerScore = task.taskContentFormAnswers[formIndex]?.score ?? 0;
        }

        // calculate score
        const newScore = prevScore - prevAnswerScore + quantity;

        // update score to player state
        response = AdjustScore(prev, newScore);

        // update specific task score
        const updatedTask = response.tasks![taskIndex];

        if (isArTask) {
          if (!updatedTask.dataARMiniGame) {
            updatedTask.dataARMiniGame = {};
          }
          updatedTask.dataARMiniGame.score = quantity;
        } else if (updatedTask.taskContentFormAnswers?.length) {
          const formIndex = updatedTask.taskContentFormAnswers.findIndex(
            (f) => f.formId === formId
          );
          if (formIndex !== -1) {
            updatedTask.taskContentFormAnswers[formIndex].score = quantity;
          }
        }

        return UpdatePlayerStateAsync(prev, response);
      });
    } else {
      setPlayer((prev) => {
        const response = GiveScore(prev, parseInt(message?.quantity));
        return UpdatePlayerStateAsync(prev, response);
      });
    }

    if (player.playerState?.teamCode) {
      await teamGiveScore(
        player.gameCode!,
        player.playerState?.teamCode,
        parseInt(message?.quantity),
        true
      );
    }

    addScoreToast(message?.quantity);
  };

  const removeScoreHandler = async (message: any) => {
    setPlayer((prev) => {
      const response = RemoveScore(prev, parseInt(message?.quantity));
      return UpdatePlayerStateAsync(prev, response);
    });

    removeScoreToast(message?.quantity);
  };

  const addRemoveTitleHandler = async (
    action: 'add' | 'remove',
    message: any
  ) => {
    setPlayer((prev) => {
      const response =
        action === 'add'
          ? AddTitle(prev, message.id)
          : RemoveTitle(prev, message.id);
      return UpdatePlayerStateAsync(prev, response);
    });

    if (action === 'add') addTitleToast(message?.id);
    if (action === 'remove') removeTitleToast(message?.id);
  };

  const addTaskHandler = async (message: any) => {
    message?.listIds?.forEach((taskId: string) => {
      const existingTask = GetTaskById(player, taskId);
      if (!existingTask) {
        const newTask: Task = {
          id: taskId,
          isVisible: true
        };

        setPlayer((prev) => {
          const response = AddTask(prev, newTask);
          return UpdatePlayerStateAsync(prev, response);
        });
      }
    });
  };

  const addRemoveTeamHandler = async (message: any) => {
    const playerResponseTeamCode = message.id?.replace(
      '00000000-0000-0000-0000-000000000000',
      ''
    );
    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        teamCode: playerResponseTeamCode
      })
    );
  };

  const timeHandler = async (
    action: 'start' | 'stop' | 'pause' | 'resume',
    message: any
  ) => {
    if (!message || !message.id) return;
    const timerId = message.id;

    if (action === 'start' || action === 'resume') timers.start(timerId);
    if (action === 'pause') timers.pause(timerId);
    if (action === 'stop') timers.stop(timerId);
  };

  const kickedHandler = async () => {
    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        status: 'kicked'
      })
    );
    timers.stopAll();
  };

  const syncPlayerState = React.useCallback(async () => {
    try {
      const now = new Date();
      const offsetMinutes = now.getTimezoneOffset();
      const adjustedTime = new Date(now.getTime() - offsetMinutes * 60 * 1000);

      const result: PlayerContextState = {
        ...player,
        playerState: {
          ...player.playerState!,
          lastUpdated: adjustedTime.toISOString()
        }
      };

      setPlayer((prev) => UpdatePlayerStateAsync(prev, result.playerState!));
      const response = await postPlayerState(
        player.gameCode!,
        player.playerState?.code!,
        JSON.stringify(result)
      );

      if (response) {
        setPlayer((prev) => mergeServerPlayerState(prev, response));
      }
    } catch (err) {
      console.error(err);
    }
  }, [player]);

  useInterval(() => {
    isNetworkOnline() && syncPlayerState();
  }, 11000);

  React.useEffect(() => {
    if (isNetworkOnline()) {
      syncPlayerState();
    }
  }, []);

  React.useEffect(() => {
    const handleOnline = () => {
      syncPlayerState();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPlayerState]);

  React.useEffect(() => {
    if (!hubConnection || !playerCode) return;

    const handler = (content: any) => {
      new ListenerManager({
        PlayerKicked: (message) => kickedHandler(),
        PlayerRemoveTitle: (message) =>
          addRemoveTitleHandler('remove', message),
        PlayerAddScore: (message) => addScoreHandler(message),
        PlayerRandomTask: (message) => addTaskHandler(message),
        PlayerDistributeTask: (message) => addTaskHandler(message),
        PlayerAddToTeam: (message) => addRemoveTeamHandler(message),
        PlayerRemoveScore: (message) => removeScoreHandler(message),
        PlayerStopTimer: (message) => timeHandler('stop', message),
        PlayerStartTimer: (message) => timeHandler('start', message),
        PlayerPauseTimer: (message) => timeHandler('pause', message),
        PlayerResumeTimer: (message) => timeHandler('resume', message),
        PlayerRemoveFromTeam: (message) => addRemoveTeamHandler(message),
        PlayerAddItem: (message) => addRemoveItemHandler('add', message),
        PlayerAddTitle: (message) => addRemoveTitleHandler('add', message),
        PlayerRemoveItem: (message) => addRemoveItemHandler('remove', message),
        PlayerShowTask: (message) => showHideObjectHandler('showTask', message),
        PlayerHideTask: (message) => showHideObjectHandler('hideTask', message),
        PlayerShowArea: (message) => showHideObjectHandler('showArea', message),
        PlayerHideArea: (message) => showHideObjectHandler('hideArea', message)
      }).listen(content);
    };

    hubConnection.on(playerCode, handler);

    return () => hubConnection.off(playerCode, handler);
  }, [hubConnection, playerCode]);

  return <></>;
};
